import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { CompanyState } from '@app/company/core/state/company.reducer';
import { ConnectionService } from '@app/connection/core/services/connection.service';
import { selectAuthIdentityCompanyId } from '@app/auth/core/state/auth.selectors';
import { isNotNullOrUndefined } from '@app/util/operators/is-not-null-or-undefined';
import { catchError, exhaustMap, map, shareReplay, retry, delay } from 'rxjs/operators';
import { Observable, of } from 'rxjs';

@Injectable()
export class CompanyLimitationService {
  public readonly connectionIds$: Observable<string[]>;

  constructor(private store$: Store<CompanyState>, private connectionService: ConnectionService) {
    const companyId$ = this.store$.pipe(
      select(selectAuthIdentityCompanyId),
      isNotNullOrUndefined(),
    );

    this.connectionIds$ = companyId$.pipe(
      exhaustMap(companyId => {
        return this.connectionService
          .search$({
            companyId,
            body: {
              connected: true,
              offset: 0,
              limit: 100,
            },
          })
          .pipe(
            retry({
              count: 2,
              delay: (error, retryCount) => {
                return of(null).pipe(delay(1000 * retryCount));
              },
            }),
            map(response => {
              const connectionIds = response.data
                .map(connection => {
                  return [connection.requestingCompanyId, connection.acceptingCompanyId];
                })
                .flat()
                .filter(id => {
                  return id !== companyId;
                }); // Remove duplicates of own company

              const allIds = [companyId, ...connectionIds];

              return allIds;
            }),
            catchError(error => {
              const fallbackIds = [companyId];

              return of(fallbackIds);
            }),
          );
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Helper method to check if any of the allowed company IDs match the user's company or connections
   * @param allowedIds Array of company IDs that are allowed access
   * @returns Observable<boolean> indicating if access is allowed
   */
  isCompanyAllowed(allowedIds: string[]): Observable<boolean> {
    if (!allowedIds || allowedIds.length === 0) {
      return of(false);
    }

    return this.connectionIds$.pipe(
      map(userCompanyIds => {
        const hasAccess = userCompanyIds.some(id => {
          return allowedIds.includes(id);
        });

        return hasAccess;
      }),
      catchError(error => {
        return of(false);
      }),
    );
  }
}
